import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './HomePage.css';

const baseUrl = import.meta.env.BASE_URL || '/';

const services = [
  {
    title: 'Operación logística',
    text: 'Diseñamos una gestión de envíos ordenada, trazable y consistente para tiendas que requieren previsibilidad operativa.',
  },
  {
    title: 'Integración comercial',
    text: 'Acompañamos la vinculación de la tienda con un proceso simple de alta, asociación y seguimiento del servicio.',
  },
  {
    title: 'Cobro y administración',
    text: 'Dejamos la operación preparada para facturar servicios, administrar pedidos y sostener un control claro del circuito.',
  },
];

const alcance = [
  'Atención a tiendas Tiendanube',
  'Cobertura urbana e interurbana',
  'Seguimiento y trazabilidad de pedidos',
  'Acompañamiento comercial y soporte operativo',
];

const metrics = [
  { value: '1', label: 'operación centralizada para la tienda' },
  { value: '3', label: 'pilares de servicio para crecer con orden' },
  { value: '100%', label: 'enfoque en relación comercial y escalabilidad' },
];

export default function HomePage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [isIframe, setIsIframe] = useState(false);

  useEffect(() => {
    setIsIframe(window.self !== window.top);
  }, []);

  const connectTiendanube = () => {
    if (!isAuthenticated || !user?.clienteId) {
      navigate('/login?returnTo=tiendanube');
      return;
    }
    if (!user?.tiendaNubeId) {
      navigate('/conectar-tienda');
      return;
    }
    navigate('/portal');
  };

  const accountHref = user?.rol === 'admin' ? '/admin' : '/portal';
  const loginHref = '/login?returnTo=tiendanube';

  const primaryCta = isAuthenticated ? (
    <Link className="landing-action-primary" to={accountHref}>
      Mi cuenta
    </Link>
  ) : (
    <Link className="landing-action-primary" to={loginHref}>
      Asociarme
    </Link>
  );

  const secondaryCta = (
    <button type="button" className="landing-action-secondary" onClick={connectTiendanube}>
      {isAuthenticated ? 'Conectar tienda' : 'Ingresar'}
    </button>
  );

  const pageBody = (
    <>
      <main>
        <section className="landing-hero" aria-label="Presentación principal">
          <div className="landing-hero-copy">
            <p className="landing-kicker">ESA Logística</p>
            <h1>Un socio operativo para tiendas que necesitan eficiencia y proyección.</h1>
            <p className="landing-lead">
              Presentamos una propuesta comercial clara para marcas que buscan ordenar su distribución, fortalecer
              la experiencia de entrega y asociarse con un operador confiable.
            </p>

            <div className="landing-hero-actions">
              {primaryCta}
              {secondaryCta}
            </div>

            <div className="landing-badges" aria-label="Beneficios principales">
              <span>Relación comercial directa</span>
              <span>Operación para e-commerce</span>
              <span>Seguimiento y control</span>
            </div>
          </div>

          <div className="landing-hero-visual">
            <div className="landing-hero-card landing-hero-card--accent">
              <p className="landing-hero-card-title">ESA Logística</p>
              <strong>Una propuesta diseñada para operar con claridad y crecer con confianza.</strong>
              <span>Imagen institucional, atención profesional y una operación pensada para escalar junto a la tienda.</span>
            </div>
            <div className="landing-hero-image">
              <img src={`${baseUrl}assets/Portada.webp`} alt="ESA Logística" />
            </div>
          </div>
        </section>

        <section className="landing-strip">
          {metrics.map((metric) => (
            <article key={metric.label} className="landing-metric">
              <strong>{metric.value}</strong>
              <span>{metric.label}</span>
            </article>
          ))}
        </section>

        <section className="landing-section" id="servicios">
          <div className="landing-section-head">
            <p className="landing-kicker">Nuestros servicios</p>
            <h2>Servicios organizados para acompañar la operación de cada tienda.</h2>
            <p>
              Estructuramos una propuesta entendible, fácil de presentar y alineada con las necesidades reales del
              negocio y del cliente final.
            </p>
          </div>

          <div className="landing-service-grid">
            {services.map((service) => (
              <article key={service.title} className="landing-service-card">
                <h3>{service.title}</h3>
                <p>{service.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="landing-section landing-section--split" id="alcance">
          <div className="landing-section-head">
            <p className="landing-kicker">Nuestro alcance</p>
            <h2>Una cobertura pensada para crecer sin perder orden operativo.</h2>
            <p>
              Mostramos de forma simple qué resolvemos, qué cubrimos y cómo se inicia la relación comercial con la
              empresa.
            </p>
          </div>

          <div className="landing-reach-card">
            {alcance.map((item) => (
              <div key={item} className="landing-reach-item">
                <span className="landing-reach-dot" aria-hidden="true" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="landing-section landing-cta" id="asociarse">
          <div>
            <p className="landing-kicker landing-kicker--inverse">Asociate con ESA Logística</p>
            <h2>Si tenés una tienda y buscás una operación más sólida, este es el punto de partida.</h2>
            <p>
              Iniciá el proceso de asociación y llevá tu tienda a un esquema de trabajo más claro, profesional y
              preparado para escalar.
            </p>
          </div>

          <div className="landing-cta-actions">
            {primaryCta}
            <button type="button" className="landing-action-secondary landing-action-secondary--light" onClick={() => navigate('/onboarding')}>
              Ver alta
            </button>
          </div>
        </section>
      </main>
    </>
  );

  const topbar = (
    <header className="landing-topbar">
      <div className="landing-topbar-inner">
        <Link to="/" className="landing-logo-button" aria-label="Ir al inicio">
          <img src={`${baseUrl}assets/LogoNavBar.svg`} alt="ESA Logística" />
        </Link>

        <nav className="landing-topbar-center" aria-label="Navegación principal">
          <a href="#servicios">Servicios</a>
          <a href="#alcance">Alcance</a>
          <a href="#asociarse">Asociarse</a>
        </nav>

        <div className="landing-topbar-right">
          {isAuthenticated ? (
            <Link className="landing-action-link" to={accountHref}>
              Mi cuenta
            </Link>
          ) : (
            <Link className="landing-action-link" to="/login">
              Ingresar
            </Link>
          )}
          {isAuthenticated ? null : (
            <Link className="landing-action-primary" to={loginHref}>
              Asociarme
            </Link>
          )}
        </div>
      </div>
    </header>
  );

  if (isIframe) {
    return (
      <div className="landing-shell landing-shell--embedded">
        <header className="landing-topbar">
          <div className="landing-topbar-inner">
            <Link to="/" className="landing-logo-button" aria-label="Ir al inicio">
              <img src={`${baseUrl}assets/LogoNavBar.svg`} alt="ESA Logística" />
            </Link>
            <div className="landing-topbar-right">
              {isAuthenticated ? (
                <Link className="landing-action-link" to={accountHref}>
                  Mi cuenta
                </Link>
              ) : (
                <Link className="landing-action-primary" to={loginHref}>
                  Asociarme
                </Link>
              )}
            </div>
          </div>
        </header>
        {pageBody}
      </div>
    );
  }

  return (
    <div className="landing-shell">
      {topbar}
      {pageBody}
    </div>
  );
}
